// Cuenta corriente / estado de cuenta por cliente: movimientos con filtros por
// dia / semana / mes / rango elegido, saldo, registro de PAGO/DEVOLUCION e
// impresion del estado de cuenta (factura interna) con logo.

import { api } from "../api";
import { formatearPesos } from "../../lib/money";
import { hoyMontevideo, rangoFechas } from "../../lib/fecha";
import { iconoImpresora } from "../iconos";
import { marcaEmpresa, abrirRemito } from "../remito";
import { abrirAnular } from "../anular";
import { nombreTurno } from "../../lib/types";
import type { Cliente, CuentaMov, PreparaPedido, PreparaItem, TipoMov, Sesion } from "../../lib/types";

// El tipo se guarda sin acento porque es un valor de la base ('DEVOLUCION').
// En el papel tiene que salir escrito bien, asi que se traduce al pintarlo.
const ETIQUETA_MOV: Record<TipoMov, string> = {
  COMPRA: "COMPRA",
  DEVOLUCION: "DEVOLUCIÓN",
  PAGO: "PAGO",
  FALTANTE: "FALTANTE",
  ANULACION: "ANULACIÓN",
};

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );
}
function ddmmaaaa(iso: string): string {
  if (!iso) return "";
  const [a, m, d] = iso.split("-");
  return `${d}/${m}/${a}`;
}

/** Una linea de un pedido, para el estado de cuenta con detalle. */
interface LineaDeCuenta {
  pedido_id: number;
  cantidad: number;
  subtotal: number;
  codigo: number;
  descripcion: string;
}

interface CuentaResp {
  cliente: { id: number; codigo: string; nombre: string };
  saldo: number;
  movimientos: CuentaMov[];
  /** Solo viene con ?detalle=1. Vacio en la carga normal. */
  lineas: LineaDeCuenta[];
}

export async function renderCuentas(contenedor: HTMLElement): Promise<void> {
  const hoy = hoyMontevideo();
  const inicioMes = hoy.slice(0, 8) + "01";
  let datos: CuentaResp | null = null;

  contenedor.innerHTML = `
    <div class="mb-4">
      <h1 class="titulo-pagina">Cuentas</h1>
      <p class="subtitulo">Estado de cuenta por cliente. Registra pagos, devoluciones y faltantes, e imprime la factura.</p>
    </div>

    <div class="no-print tarjeta mb-4 flex flex-wrap items-end gap-3">
      <label class="min-w-[16rem] flex-1"><span class="etiqueta">Cliente</span>
        <select id="cliente" class="campo"><option value="">Cargando...</option></select></label>
      <label><span class="etiqueta">Desde</span><input id="desde" type="date" class="campo" value="${inicioMes}" /></label>
      <label><span class="etiqueta">Hasta</span><input id="hasta" type="date" class="campo" value="${hoy}" /></label>
      <div class="flex gap-1">
        <button class="btn-sec text-sm" data-rango="dia">Día</button>
        <button class="btn-sec text-sm" data-rango="semana">Semana</button>
        <button class="btn-sec text-sm" data-rango="mes">Mes</button>
        <button class="btn-sec text-sm" data-rango="todo">Todo</button>
      </div>
      <button id="detalle" type="button" class="btn-sec" disabled aria-pressed="false" title="Mostrar los artículos de cada compra">Con detalles</button>
      <button id="imprimir" class="btn-sec" disabled title="Imprime el estado de cuenta tal como se ve en pantalla">${iconoImpresora} Imprimir</button>
      <button id="permitirAnular" class="btn-sec hidden" title="Muestra el botón Anular en cada compra"></button>
    </div>

    <div id="salida"></div>

    <div class="no-print tarjeta mt-4">
      <h2 class="mb-2 text-sm font-bold uppercase tracking-wide text-masa-600">Registrar movimiento</h2>
      <form id="fmov" class="flex flex-wrap items-end gap-3">
        <label><span class="etiqueta">Tipo</span>
          <select id="m_tipo" class="campo"><option value="PAGO">Pago</option><option value="DEVOLUCION">Devolución</option><option value="FALTANTE">Faltante</option></select></label>
        <label><span class="etiqueta">Monto</span><input id="m_monto" type="number" min="0" class="campo w-32" /></label>
        <label><span class="etiqueta">Fecha</span><input id="m_fecha" type="date" class="campo" value="${hoy}" /></label>
        <label class="min-w-[12rem] flex-1"><span class="etiqueta">Notas</span><input id="m_notas" class="campo" placeholder="Opcional" /></label>
        <button class="btn-primario">Registrar</button>
        <span id="m_err" class="self-center text-sm text-red-600"></span>
      </form>
      <p id="m_ayuda" class="mt-2 hidden text-sm text-masa-500">Elegí los artículos abajo: el monto se calcula solo con la lista del cliente.</p>
      <div id="picker" class="mt-3 hidden space-y-2"></div>
    </div>

    <dialog id="dlgRemito" class="p-0"></dialog>
    <dialog id="dlgAnular"></dialog>`;

  const selCliente = contenedor.querySelector("#cliente") as HTMLSelectElement;
  const inpDesde = contenedor.querySelector("#desde") as HTMLInputElement;
  const inpHasta = contenedor.querySelector("#hasta") as HTMLInputElement;
  const salida = contenedor.querySelector("#salida") as HTMLElement;
  const btnDetalle = contenedor.querySelector("#detalle") as HTMLButtonElement;
  const btnImprimir = contenedor.querySelector("#imprimir") as HTMLButtonElement;
  const btnPermitir = contenedor.querySelector("#permitirAnular") as HTMLButtonElement;
  const mErr = contenedor.querySelector("#m_err") as HTMLElement;
  const selTipo = contenedor.querySelector("#m_tipo") as HTMLSelectElement;
  const inpMonto = contenedor.querySelector("#m_monto") as HTMLInputElement;
  const mAyuda = contenedor.querySelector("#m_ayuda") as HTMLElement;
  const picker = contenedor.querySelector("#picker") as HTMLElement;

  // Estado del picker de items (para DEVOLUCION / FALTANTE).
  let prepara: PreparaPedido | null = null; // catalogo + precios de la lista del cliente
  let preparaClienteId = 0;

  // Con detalle = debajo de cada COMPRA van los articulos y las cantidades de
  // ese pedido. Al entrar arranca SIN detalle; el boton "Con detalles" lo
  // alterna, y la eleccion dura mientras se permanezca en Cuentas (aunque se
  // cambie de cliente o de fechas). Imprimir saca lo mismo que se ve.
  //
  // Van declaradas ACA, antes de la carga de clientes: con un solo cliente esa
  // carga llama a cargar() -> pintar() -> detalleDe(), que las lee. Declararlas
  // mas abajo las dejaba sin inicializar en ese momento y la pantalla quedaba
  // colgada en "Cargando..." con un "Error al cargar clientes" que despistaba.
  let conDetalle = false;
  let lineasPorPedido = new Map<number, LineaDeCuenta[]>();
  // Numero de la carga en curso: si el usuario cambia de cliente antes de que
  // vuelva la anterior, la respuesta vieja se descarta en vez de pisar la nueva.
  let ultimaCarga = 0;
  const cant = new Map<number, number>(); // producto_id -> cantidad
  const precioDe = new Map<number, number>();

  // Las dos llamadas van juntas pero se resuelven por separado: si falla la
  // sesion, el selector NO tiene que decir "Error al cargar clientes" y mandar
  // a buscar donde no es. Con allSettled ninguna de las dos queda un instante
  // sin catch, que es lo que pasaria arrancandolas y esperandolas una por una.
  const [rSesion, rClientes] = await Promise.allSettled([
    api.get<Sesion>("/api/sesion"),
    api.get<Cliente[]>("/api/clientes?activo=1"),
  ]);

  // Anular un pedido mueve la produccion y los informes, no solo la cuenta: es
  // una operacion de pedidos. Si la sesion no tiene ese permiso el boton NO se
  // dibuja, en vez de aparecer y devolver 403 recien al tocarlo.
  let puedeAnular = false;

  /**
   * Los botones Anular arrancan ESCONDIDOS y hay que habilitarlos a mano.
   *
   * Es una accion destructiva a un toque de distancia dentro de una tabla que
   * en el celular se lee scrolleando: sin esta traba, un dedo mal apoyado
   * mientras se revisa el estado de cuenta anula un pedido. Habilitarlas es un
   * gesto deliberado, y vuelve a apagarse cada vez que se entra a la pantalla.
   */
  let anulacionesVisibles = false;

  function pintarBotonPermitir(): void {
    btnPermitir.classList.toggle("hidden", !puedeAnular);
    btnPermitir.textContent = anulacionesVisibles
      ? "Anulaciones habilitadas"
      : "Habilitar anulaciones";
    btnPermitir.classList.toggle("btn-peligro", anulacionesVisibles);
    btnPermitir.classList.toggle("btn-sec", !anulacionesVisibles);
  }
  if (rSesion.status === "fulfilled") {
    puedeAnular = rSesion.value.es_admin || rSesion.value.permisos.includes("anular");
  }
  pintarBotonPermitir();

  btnPermitir.addEventListener("click", () => {
    anulacionesVisibles = !anulacionesVisibles;
    pintarBotonPermitir();
    pintar();
  });

  let unicoCliente = 0;
  if (rClientes.status === "fulfilled") {
    const clientes = rClientes.value;
    selCliente.innerHTML =
      `<option value="">Elegir cliente...</option>` +
      clientes.map((c) => `<option value="${c.id}">${esc(c.codigo)} — ${esc(c.nombre)}</option>`).join("");
    if (clientes.length === 1) unicoCliente = clientes[0].id;
  } else {
    selCliente.innerHTML = `<option value="">Error al cargar clientes</option>`;
  }
  // Con un solo cliente no hay nada que elegir: se abre su estado de cuenta.
  // Va FUERA del try de arriba: si falla la cuenta, el error es de la cuenta y
  // no "no se pudieron cargar los clientes", que manda a buscar donde no es.
  if (unicoCliente) {
    selCliente.value = String(unicoCliente);
    await cargar();
  }

  function indexarLineas(lineas: LineaDeCuenta[]): void {
    lineasPorPedido = new Map();
    for (const l of lineas) {
      const arr = lineasPorPedido.get(l.pedido_id) ?? [];
      arr.push(l);
      lineasPorPedido.set(l.pedido_id, arr);
    }
  }

  async function cargar(): Promise<void> {
    const carga = ++ultimaCarga;
    const id = Number(selCliente.value);
    datos = null;
    lineasPorPedido.clear();
    btnDetalle.disabled = true;
    btnImprimir.disabled = true;
    if (!id) {
      salida.innerHTML = "";
      return;
    }
    salida.innerHTML = `<div class="tarjeta text-masa-400">Cargando...</div>`;
    try {
      // El detalle se trae SIEMPRE, se muestre o no: asi alternarlo es
      // instantaneo y no hace otra consulta.
      const cuenta = await api.get<CuentaResp>(`/api/clientes/${id}/cuenta?detalle=1`);
      if (carga !== ultimaCarga) return;
      datos = cuenta;
      indexarLineas(datos.lineas ?? []);
      btnDetalle.disabled = false;
      btnImprimir.disabled = false;
      pintar();
    } catch (e) {
      if (carga !== ultimaCarga) return;
      salida.innerHTML = `<div class="tarjeta text-red-700">${esc(
        e instanceof Error ? e.message : "No se pudo cargar la cuenta",
      )}</div>`;
    }
  }

  /**
   * Las lineas de un pedido, en letra chica debajo de su COMPRA.
   *
   * El bloque llega SOLO hasta la columna Compra (colspan 2, contando Detalle):
   * las columnas Devolucion, Faltante, Pago y Saldo quedan libres. Ocupando
   * todo el ancho el detalle se metia debajo de esas columnas y el papel
   * quedaba sucio; angosto, el texto baja en mas renglones —y si hace falta
   * otra hoja, se hace— pero las columnas de la derecha se leen limpias.
   */
  function detalleDe(mov: CuentaMov): string {
    if (!conDetalle || !mov.pedido_id) return "";
    const lineas = lineasPorPedido.get(mov.pedido_id);
    if (!lineas || lineas.length === 0) return "";
    // Separador entre articulos: en una columna angosta el importe de uno y la
    // cantidad del siguiente quedan pegados y se leen como un solo numero
    // ("$ 50" + "2 Empanada" = "$ 502").
    const items = lineas
      .map(
        (l) =>
          `<span class="inline-block whitespace-nowrap"><b>${esc(l.cantidad)}</b> ${esc(l.descripcion)}
             <span class="text-masa-400">${formatearPesos(l.subtotal)}</span></span>`,
      )
      .join(`<span class="px-1.5 text-masa-300">·</span>`);
    return `<tr class="align-top">
      <td></td>
      <td colspan="2" class="pb-1 pl-2 text-[11px] leading-relaxed text-masa-600">${items}</td>
      <td></td><td></td><td></td><td></td>
    </tr>`;
  }

  function pintar(): void {
    if (!datos) return;
    const desde = inpDesde.value;
    const hasta = inpHasta.value;

    // Movimientos ordenados asc con saldo acumulado.
    const movs = [...datos.movimientos].sort((x, y) =>
      x.fecha === y.fecha ? x.id - y.id : x.fecha < y.fecha ? -1 : 1,
    );
    const signo = (t: string) => (t === "COMPRA" ? 1 : -1);
    let acum = 0;
    let saldoAnterior = 0;
    const filas: Array<{ mov: CuentaMov; saldo: number }> = [];
    for (const m of movs) {
      acum += signo(m.tipo) * m.monto;
      if (desde && m.fecha < desde) { saldoAnterior = acum; continue; }
      if (hasta && m.fecha > hasta) continue;
      filas.push({ mov: m, saldo: acum });
    }

    // La ANULACION va en la columna Compra, en negativo: es el reverso de esa
    // compra, no otra clase de movimiento. Asi el total de la columna queda en
    // las compras REALES —las que quedaron en pie— y el papel no gana columnas.
    const tot = { compra: 0, devolucion: 0, faltante: 0, pago: 0 };
    for (const { mov } of filas) {
      if (mov.tipo === "COMPRA") tot.compra += mov.monto;
      else if (mov.tipo === "ANULACION") tot.compra -= mov.monto;
      else if (mov.tipo === "DEVOLUCION") tot.devolucion += mov.monto;
      else if (mov.tipo === "FALTANTE") tot.faltante += mov.monto;
      else tot.pago += mov.monto;
    }

    // Pedidos que ya tienen su contra-asiento: no se ofrece anularlos de nuevo
    // (daria 409) ni se pone el boton en la fila de la propia anulacion.
    const yaAnulados = new Set(
      datos.movimientos.filter((m) => m.tipo === "ANULACION").map((m) => m.pedido_id),
    );
    const saldoFinal = filas.length ? filas[filas.length - 1].saldo : saldoAnterior;

    const cli = datos.cliente;
    const periodo = desde || hasta ? `${ddmmaaaa(desde) || "inicio"} — ${ddmmaaaa(hasta) || "hoy"}` : "Todo el historial";

    const filasHtml = filas
      .map(
        ({ mov, saldo }) => `<tr class="border-t border-crema-100">
          <td class="py-1 pr-2 whitespace-nowrap">${ddmmaaaa(mov.fecha)}</td>
          <td class="py-1 pr-2">${esc(ETIQUETA_MOV[mov.tipo] ?? mov.tipo)}${
            mov.pedido_id
              ? ` <button type="button" data-remito="${mov.pedido_id}"
                    class="no-print rounded px-1 text-xs font-semibold text-acento-700 underline underline-offset-2 hover:bg-acento-50"
                    title="Ver cómo se formó este pedido">#${mov.pedido_id}</button>
                  <span class="hidden text-xs text-masa-400 print:inline">#${mov.pedido_id}</span>${
                    puedeAnular && anulacionesVisibles && mov.tipo === "COMPRA" && !yaAnulados.has(mov.pedido_id)
                      ? ` <button type="button" data-anular="${mov.pedido_id}"
                            class="no-print rounded px-1 text-xs font-semibold text-red-700 underline underline-offset-2 hover:bg-red-50"
                            title="Anular este pedido: deshace la compra">Anular</button>`
                      : ""
                  }`
              : ""
          }${mov.turno ? ` <span class="text-xs text-masa-400">${esc(nombreTurno(mov.turno))}</span>` : ""}${mov.notas ? ` <span class="text-xs text-masa-400">${esc(mov.notas)}</span>` : ""}</td>
          <td class="py-1 pr-2 text-right tabular-nums">${
            mov.tipo === "COMPRA"
              ? formatearPesos(mov.monto)
              : mov.tipo === "ANULACION"
                ? `−${formatearPesos(mov.monto)}`
                : ""
          }</td>
          <td class="py-1 pr-2 text-right tabular-nums">${mov.tipo === "DEVOLUCION" ? formatearPesos(mov.monto) : ""}</td>
          <td class="py-1 pr-2 text-right tabular-nums">${mov.tipo === "FALTANTE" ? formatearPesos(mov.monto) : ""}</td>
          <td class="py-1 pr-2 text-right tabular-nums">${mov.tipo === "PAGO" ? formatearPesos(mov.monto) : ""}</td>
          <td class="py-1 text-right font-medium tabular-nums">${formatearPesos(saldo)}</td>
        </tr>${detalleDe(mov)}`,
      )
      .join("");

    salida.innerHTML = `
      <div class="tarjeta bg-white print:border-0 print:shadow-none">
        <div class="mb-3 flex items-center justify-between gap-4">
          ${marcaEmpresa("grande")}
          <div class="text-right text-sm">
            <div class="text-lg font-bold">Estado de cuenta</div>
            <div>${esc(cli.nombre)} (${esc(cli.codigo)})</div>
            <div class="text-masa-500">${esc(periodo)}</div>
          </div>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead class="bg-crema-100 text-[11px] uppercase tracking-wider text-masa-500"><tr>
              <th class="px-2 py-2 text-left font-semibold">Fecha</th>
              <th class="px-2 py-2 text-left font-semibold">Detalle</th>
              <th class="px-2 py-2 text-right font-semibold">Compra</th>
              <th class="px-2 py-2 text-right font-semibold">Devolución</th>
              <th class="px-2 py-2 text-right font-semibold">Faltante</th>
              <th class="px-2 py-2 text-right font-semibold">Pago</th>
              <th class="px-2 py-2 text-right font-semibold">Saldo acumulado</th>
            </tr></thead>
            <tbody>
              <tr class="text-masa-500"><td class="px-2 py-1" colspan="6">Saldo antes de este periodo</td><td class="px-2 py-1 text-right tabular-nums">${formatearPesos(saldoAnterior)}</td></tr>
              ${filasHtml || `<tr><td colspan="7" class="px-2 py-4 text-center text-masa-400">Sin movimientos en el periodo.</td></tr>`}
            </tbody>
            <tfoot>
              <tr class="border-t-2 border-masa-300 font-semibold">
                <td class="px-2 py-2" colspan="2">Totales del periodo</td>
                <td class="px-2 py-2 text-right tabular-nums">${formatearPesos(tot.compra)}</td>
                <td class="px-2 py-2 text-right tabular-nums">${formatearPesos(tot.devolucion)}</td>
                <td class="px-2 py-2 text-right tabular-nums">${formatearPesos(tot.faltante)}</td>
                <td class="px-2 py-2 text-right tabular-nums">${formatearPesos(tot.pago)}</td>
                <td class="px-2 py-2 text-right tabular-nums">
                  <span class="block text-[10px] font-normal uppercase tracking-wider text-masa-400">Saldo al cierre</span>
                  ${formatearPesos(saldoFinal)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        <!-- Dos saldos distintos que antes se llamaban los dos "saldo": el del
             cierre del periodo (arriba) y lo que el cliente debe HOY. Cuando el
             filtro es "Todo" dan igual, y ahí es donde más confundia. -->
        <div class="mt-3 flex flex-col items-end">
          <div>
            <span class="text-sm text-masa-500">Deuda total de la cuenta: </span>
            <span class="text-xl font-bold">${formatearPesos(datos.saldo)}</span>
          </div>
          <p class="mt-0.5 max-w-md text-right text-xs text-masa-400">
            Todos los pedidos confirmados menos los pagos, devoluciones y faltantes${
              desde || hasta ? ", tambien los de fuera de este periodo" : ""
            }.
            ${
              Math.round(datos.saldo) !== Math.round(saldoFinal)
                ? `Dentro del periodo elegido el saldo cierra en <b>${formatearPesos(saldoFinal)}</b>.`
                : ""
            }
          </p>
        </div>
      </div>`;
  }

  // ------- Picker de items para DEVOLUCION / FALTANTE (monto calculado) -------
  const porItems = (): boolean => selTipo.value !== "PAGO";

  function recalcularMonto(): void {
    let total = 0;
    for (const [pid, q] of cant) total += q * (precioDe.get(pid) ?? 0);
    total = Math.round(total * 100) / 100;
    inpMonto.value = total ? String(total) : "";
  }

  function pintarPicker(): void {
    if (!prepara) return;
    const porCat = new Map<number, PreparaItem[]>();
    for (const it of prepara.items) {
      if (!(it.precio > 0)) continue;
      const arr = porCat.get(it.producto.categoria_id) ?? [];
      arr.push(it);
      porCat.set(it.producto.categoria_id, arr);
    }
    const nombreCat = new Map(prepara.categorias.map((c) => [c.id, c.nombre]));
    const bloques: string[] = [];
    for (const c of prepara.categorias) {
      const items = porCat.get(c.id);
      if (!items || items.length === 0) continue;
      const cargados = items.reduce((n, it) => n + ((cant.get(it.producto.id) ?? 0) > 0 ? 1 : 0), 0);
      const filas = items
        .map((it) => {
          const pid = it.producto.id;
          const q = cant.get(pid) ?? 0;
          return `<div class="flex items-center gap-2 border-t border-crema-100 py-1.5">
            <div class="flex-1">
              <div class="text-sm text-masa-800">${esc(it.producto.descripcion)}</div>
              <div class="text-xs text-masa-400">${formatearPesos(it.precio)}</div>
            </div>
            <button type="button" class="chip-accion" data-step="-1" data-pid="${pid}" aria-label="Quitar">−</button>
            <input class="campo cant w-20 text-center" type="number" min="0" step="any" inputmode="decimal" data-pid="${pid}" value="${q || ""}" placeholder="0" />
            <button type="button" class="chip-accion" data-step="1" data-pid="${pid}" aria-label="Sumar">+</button>
          </div>`;
        })
        .join("");
      bloques.push(`<details class="rounded-xl border border-crema-200" ${cargados ? "open" : ""}>
        <summary class="cursor-pointer px-3 py-2 text-sm font-semibold text-masa-700">${esc(nombreCat.get(c.id) ?? "Otros")}${cargados ? ` <span class="text-xs font-normal text-masa-400">(${cargados})</span>` : ""}</summary>
        <div class="px-3 pb-2">${filas}</div>
      </details>`);
    }
    picker.innerHTML =
      bloques.join("") ||
      `<div class="tarjeta text-masa-400">El cliente no tiene productos con precio en su lista.</div>`;
  }

  async function cargarPrepara(id: number): Promise<void> {
    picker.innerHTML = `<div class="tarjeta text-masa-400">Cargando artículos...</div>`;
    try {
      prepara = await api.get<PreparaPedido>(`/api/pedidos/preparar?cliente_id=${id}`);
      preparaClienteId = id;
      precioDe.clear();
      for (const it of prepara.items) precioDe.set(it.producto.id, it.precio);
    } catch (e) {
      prepara = null;
      picker.innerHTML = `<div class="tarjeta text-red-700">${esc(e instanceof Error ? e.message : "Error")}</div>`;
    }
  }

  async function actualizarModo(): Promise<void> {
    const items = porItems();
    mAyuda.classList.toggle("hidden", !items);
    picker.classList.toggle("hidden", !items);
    inpMonto.readOnly = items;
    inpMonto.classList.toggle("bg-crema-100", items);
    inpMonto.classList.toggle("text-masa-500", items);
    if (!items) {
      inpMonto.value = "";
      return;
    }
    const id = Number(selCliente.value);
    if (!id) {
      picker.innerHTML = `<div class="tarjeta text-masa-400">Elegí un cliente primero.</div>`;
      inpMonto.value = "";
      return;
    }
    if (!prepara || preparaClienteId !== id) await cargarPrepara(id);
    pintarPicker();
    recalcularMonto();
  }

  picker.addEventListener("input", (ev) => {
    const t = ev.target as HTMLInputElement;
    if (!t.classList.contains("cant")) return;
    const pid = Number(t.dataset.pid);
    const v = Math.max(0, Number(t.value.replace(",", ".")) || 0);
    if (v > 0) cant.set(pid, v);
    else cant.delete(pid);
    recalcularMonto();
  });

  picker.addEventListener("click", (ev) => {
    const b = (ev.target as HTMLElement).closest("[data-step]") as HTMLElement | null;
    if (!b) return;
    const pid = Number(b.dataset.pid);
    const delta = Number(b.dataset.step);
    const nuevo = Math.max(0, (cant.get(pid) ?? 0) + delta);
    if (nuevo > 0) cant.set(pid, nuevo);
    else cant.delete(pid);
    const inp = picker.querySelector(`input.cant[data-pid="${pid}"]`) as HTMLInputElement | null;
    if (inp) inp.value = nuevo ? String(nuevo) : "";
    recalcularMonto();
  });

  selTipo.addEventListener("change", () => void actualizarModo());

  selCliente.addEventListener("change", () => {
    cant.clear();
    prepara = null;
    preparaClienteId = 0;
    void cargar();
    void actualizarModo();
  });
  inpDesde.addEventListener("change", pintar);
  inpHasta.addEventListener("change", pintar);

  contenedor.querySelectorAll<HTMLButtonElement>("[data-rango]").forEach((b) => {
    b.addEventListener("click", () => {
      const tipo = b.dataset.rango as "dia" | "semana" | "mes" | "todo";
      if (tipo === "todo") { inpDesde.value = ""; inpHasta.value = ""; }
      else {
        const ref = inpHasta.value || hoy;
        const [d, h] = rangoFechas(ref, tipo);
        inpDesde.value = d; inpHasta.value = h;
      }
      pintar();
    });
  });

  // El boton alterna entre ver y no ver los articulos de cada compra. Dice lo
  // que va a pasar al tocarlo: "Con detalles" cuando estan ocultos, "Sin
  // detalles" cuando estan a la vista.
  btnDetalle.addEventListener("click", () => {
    conDetalle = !conDetalle;
    btnDetalle.textContent = conDetalle ? "Sin detalles" : "Con detalles";
    btnDetalle.title = conDetalle
      ? "Ocultar los artículos de cada compra"
      : "Mostrar los artículos de cada compra";
    btnDetalle.setAttribute("aria-pressed", String(conDetalle));
    pintar();
  });

  // La hoja y el PDF salen tal cual se ven: con el modo y el periodo elegidos.
  btnImprimir.addEventListener("click", () => window.print());

  // Tocar el numero de pedido abre el remito: ahi se ve como se formo ese
  // pedido (articulos, cantidades y precios congelados) y se puede imprimir.
  // Es el mismo remito de Pedidos web, no una vista nueva.
  const dlgRemito = contenedor.querySelector("#dlgRemito") as HTMLDialogElement;
  const dlgAnular = contenedor.querySelector("#dlgAnular") as HTMLDialogElement;

  salida.addEventListener("click", (ev) => {
    const objetivo = ev.target as HTMLElement;

    // El cartel es el compartido con la pantalla de Pedidos (src/app/anular.ts).
    // Los datos salen de la fila que ya esta pintada: ninguna llamada extra.
    const anular = objetivo.closest<HTMLElement>("[data-anular]");
    if (anular) {
      const pedidoId = Number(anular.dataset.anular);
      const mov = datos?.movimientos.find((m) => m.pedido_id === pedidoId && m.tipo === "COMPRA");
      if (!datos || !mov) return;
      abrirAnular(
        dlgAnular,
        {
          id: pedidoId,
          cliente: datos.cliente.nombre,
          fecha_entrega: mov.fecha_entrega ?? mov.fecha,
          turno: mov.turno,
          total: mov.monto,
        },
        // La compra sigue ahi y aparece la ANULACION debajo, con el saldo
        // volviendo a lo que era. Eso es la confirmacion visual: no hace falta
        // ningun cartel, y de paso se ve que quedo registrado.
        cargar,
      );
      return;
    }

    const b = objetivo.closest<HTMLElement>("[data-remito]");
    if (b) void abrirRemito(dlgRemito, Number(b.dataset.remito));
  });

  // Registrar PAGO (monto) / DEVOLUCION / FALTANTE (por items, monto calculado)
  const fmov = contenedor.querySelector("#fmov") as HTMLFormElement;
  fmov.addEventListener("submit", async (ev) => {
    ev.preventDefault();
    mErr.textContent = "";
    const id = Number(selCliente.value);
    if (!id) { mErr.textContent = "Elegí un cliente primero"; return; }
    const tipo = selTipo.value;
    const fecha = (contenedor.querySelector("#m_fecha") as HTMLInputElement).value;
    const notas = (contenedor.querySelector("#m_notas") as HTMLInputElement).value.trim() || null;

    let cuerpo: Record<string, unknown>;
    if (porItems()) {
      const items = [...cant.entries()].map(([producto_id, cantidad]) => ({ producto_id, cantidad }));
      if (items.length === 0) { mErr.textContent = "Elegí al menos un artículo"; return; }
      cuerpo = { tipo, items, fecha, notas };
    } else {
      const monto = Number(inpMonto.value);
      if (!(monto > 0)) { mErr.textContent = "Monto invalido"; return; }
      cuerpo = { tipo, monto, fecha, notas };
    }

    try {
      await api.post(`/api/clientes/${id}/cuenta`, cuerpo);
      inpMonto.value = "";
      (contenedor.querySelector("#m_notas") as HTMLInputElement).value = "";
      cant.clear();
      await cargar();
      await actualizarModo();
    } catch (e) {
      mErr.textContent = e instanceof Error ? e.message : "Error";
    }
  });
}
